import { supabase } from '../config/supabase';
import {
  SellerPayout,
  SellerPayoutDetails,
  ClubPayout,
  ClubPendingSummary,
  PayoutStatus,
} from '../types';

/**
 * Service for managing seller and club payouts
 */
export const payoutsService = {
  // ============================================================================
  // SELLER PAYOUTS
  // ============================================================================

  /**
   * Get all seller payouts, optionally filtered by status
   */
  async getSellerPayouts(status?: PayoutStatus): Promise<SellerPayout[]> {
    let query = supabase
      .from('seller_payouts')
      .select(`
        *,
        seller:profiles!seller_id(id, first_name, last_name, email),
        transaction:transactions!transaction_id(
          id,
          item_id,
          agreed_price,
          seller_amount,
          seller_iban,
          seller_account_holder,
          created_at,
          item:clothing_items!item_id(id, title, image_urls)
        )
      `)
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Get detailed seller payout information for admin view
   */
  async getSellerPayoutDetails(payoutId: string): Promise<SellerPayoutDetails> {
    const { data, error } = await supabase
      .from('seller_payouts')
      .select(`
        *,
        seller:profiles!seller_id(id, first_name, last_name, email, phone),
        transaction:transactions!transaction_id(
          *,
          item:clothing_items!item_id(id, title, image_urls, size)
        )
      `)
      .eq('id', payoutId)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Payout not found');
    return data as SellerPayoutDetails;
  },

  /**
   * Mark a seller payout as paid
   */
  async markSellerPayoutPaid(payoutId: string, reference: string): Promise<void> {
    const { error } = await supabase
      .from('seller_payouts')
      .update({
        status: 'paid',
        paid_at: new Date().toISOString(),
        reference,
        failure_reason: null,
        failed_at: null,
      })
      .eq('id', payoutId);

    if (error) throw error;
  },

  /**
   * Mark a seller payout as failed
   */
  async markSellerPayoutFailed(payoutId: string, reason: string): Promise<void> {
    const { error } = await supabase
      .from('seller_payouts')
      .update({
        status: 'failed',
        failed_at: new Date().toISOString(),
        failure_reason: reason,
      })
      .eq('id', payoutId);

    if (error) throw error;
  },

  /**
   * Update IBAN on transaction and reset payout to pending for retry
   */
  async updateSellerPayoutIban(
    payoutId: string,
    transactionId: string,
    iban: string,
    accountHolder: string
  ): Promise<void> {
    // Update IBAN on transaction
    const { error: txError } = await supabase
      .from('transactions')
      .update({
        seller_iban: iban,
        seller_account_holder: accountHolder,
      })
      .eq('id', transactionId);

    if (txError) throw txError;

    // Reset payout to pending
    const { error: payoutError } = await supabase
      .from('seller_payouts')
      .update({
        status: 'pending',
        failed_at: null,
        failure_reason: null,
      })
      .eq('id', payoutId);

    if (payoutError) throw payoutError;
  },

  // ============================================================================
  // CLUB PAYOUTS
  // ============================================================================

  /**
   * Get clubs with pending payouts (transactions not yet linked to a club payout)
   */
  async getClubsWithPendingPayouts(): Promise<ClubPendingSummary[]> {
    // First, get all transaction IDs that are already linked to a club payout
    const { data: existingLinks, error: linksError } = await supabase
      .from('club_payout_transactions')
      .select('transaction_id');

    if (linksError) throw linksError;

    const linkedTransactionIds = new Set(
      (existingLinks || []).map((link) => link.transaction_id)
    );

    // Get completed transactions
    const { data, error } = await supabase
      .from('transactions')
      .select(`
        id,
        club_total_amount,
        item:clothing_items!item_id(
          club:clubs!club_id(id, name)
        )
      `)
      .eq('status', 'completed');

    if (error) throw error;

    // Aggregate by club, excluding transactions already in a club payout
    const clubMap = new Map<string, ClubPendingSummary>();

    for (const tx of data || []) {
      // Skip transactions already linked to a club payout
      if (linkedTransactionIds.has(tx.id)) continue;

      // Type assertion needed due to Supabase nested join type inference
      const item = tx.item as unknown as { club: { id: string; name: string } } | null;
      const club = item?.club;
      if (!club) continue;

      const existing = clubMap.get(club.id);
      if (existing) {
        existing.pending_amount += tx.club_total_amount || 0;
        existing.transaction_count += 1;
      } else {
        clubMap.set(club.id, {
          club_id: club.id,
          club_name: club.name,
          pending_amount: tx.club_total_amount || 0,
          transaction_count: 1,
        });
      }
    }

    return Array.from(clubMap.values());
  },

  /**
   * Create a club payout for all pending transactions
   */
  async createClubPayout(clubId: string): Promise<ClubPayout> {
    // First, get all transaction IDs that are already linked to a club payout
    const { data: existingLinks, error: linksError } = await supabase
      .from('club_payout_transactions')
      .select('transaction_id');

    if (linksError) throw linksError;

    const linkedTransactionIds = new Set(
      (existingLinks || []).map((link) => link.transaction_id)
    );

    // Get completed transactions for this club
    const { data: transactions, error: txError } = await supabase
      .from('transactions')
      .select(`
        id,
        club_total_amount,
        item:clothing_items!item_id(club_id)
      `)
      .eq('status', 'completed');

    if (txError) throw txError;

    // Filter to this club's transactions that aren't already in a payout
    // Type assertion needed due to Supabase nested join type inference
    const clubTransactions = (transactions || []).filter((tx) => {
      // Skip transactions already linked to a club payout
      if (linkedTransactionIds.has(tx.id)) return false;

      const item = tx.item as unknown as { club_id: string } | null;
      return item?.club_id === clubId;
    });

    if (clubTransactions.length === 0) {
      throw new Error('No pending transactions for this club');
    }

    // Calculate total
    const totalAmount = clubTransactions.reduce(
      (sum, tx) => sum + (tx.club_total_amount || 0),
      0
    );

    // Create club payout
    const { data: payout, error: payoutError } = await supabase
      .from('club_payouts')
      .insert({
        club_id: clubId,
        amount: totalAmount,
      })
      .select()
      .single();

    if (payoutError) throw payoutError;

    // Link transactions to payout
    const junctionRows = clubTransactions.map((tx) => ({
      club_payout_id: payout.id,
      transaction_id: tx.id,
    }));

    const { error: junctionError } = await supabase
      .from('club_payout_transactions')
      .insert(junctionRows);

    if (junctionError) throw junctionError;

    return payout;
  },

  /**
   * Get club payouts, optionally filtered by club and/or status
   */
  async getClubPayouts(
    clubId?: string,
    status?: PayoutStatus
  ): Promise<ClubPayout[]> {
    let query = supabase
      .from('club_payouts')
      .select(`
        *,
        club:clubs!club_id(id, name)
      `)
      .order('created_at', { ascending: false });

    if (clubId) {
      query = query.eq('club_id', clubId);
    }
    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Get club payout details with linked transactions
   */
  async getClubPayoutDetails(payoutId: string): Promise<ClubPayout> {
    const { data: payout, error: payoutError } = await supabase
      .from('club_payouts')
      .select(`
        *,
        club:clubs!club_id(id, name)
      `)
      .eq('id', payoutId)
      .single();

    if (payoutError) throw payoutError;

    // Get linked transactions
    const { data: links, error: linksError } = await supabase
      .from('club_payout_transactions')
      .select(`
        transaction:transactions!transaction_id(
          id,
          club_total_amount,
          created_at,
          item:clothing_items!item_id(title)
        )
      `)
      .eq('club_payout_id', payoutId);

    if (linksError) throw linksError;

    return {
      ...payout,
      transactions: links?.map((l) => l.transaction) || [],
    };
  },

  /**
   * Mark a club payout as paid
   */
  async markClubPayoutPaid(payoutId: string, reference: string): Promise<void> {
    const { error } = await supabase
      .from('club_payouts')
      .update({
        status: 'paid',
        paid_at: new Date().toISOString(),
        reference,
        failure_reason: null,
        failed_at: null,
      })
      .eq('id', payoutId);

    if (error) throw error;
  },

  /**
   * Mark a club payout as failed
   */
  async markClubPayoutFailed(payoutId: string, reason: string): Promise<void> {
    const { error } = await supabase
      .from('club_payouts')
      .update({
        status: 'failed',
        failed_at: new Date().toISOString(),
        failure_reason: reason,
      })
      .eq('id', payoutId);

    if (error) throw error;
  },
};
